import React, { useState } from 'react';
import DropdownMenu, { DropdownItemRadio, DropdownItemGroupRadio } from '@atlaskit/dropdown-menu';
import Button from '@atlaskit/button';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import Form, { Field } from '@atlaskit/form';
import Badge from '@atlaskit/badge';
import { DatePicker } from '@atlaskit/datetime-picker';
import './date-filter.scss';

export type DateRangeType = {
  from: string;
  to: string;
};

export type DateRangeOption = {
  text: string;
  value: number;
  from?: Date | number;
  to?: Date | number;
};

interface Props {
  selected?: string;
  // eslint-disable-next-line no-unused-vars
  dateValue: (dateRange: DateRangeType, isCustomFilter?: boolean) => void;
  // eslint-disable-next-line no-unused-vars
  onSelected?: (selected: string) => void;
  placeholder?: string;
  dateRangeOptions?: DateRangeOption[];
}

const rangeOption = [
  { text: 'Last 24 hours', value: 1 },
  { text: 'Last 7 days', value: 7 },
  { text: 'Last 14 days', value: 14 },
  { text: 'Last 30 days', value: 30 },
  { text: 'Last 90 days', value: 90 },
  { text: 'Last 12 months', value: 365 },
  { text: 'Custom', value: 0 },
];

export const getTimeStampString = (date: Date) => {
  return (date.getTime() * 1000).toString();
};

const DateFilterWithBadge = (props: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const dateRangeOptions: DateRangeOption[] = props.dateRangeOptions ?? rangeOption;
  const [customDateRange, setCustomDateRange] = useState<DateRangeType>({
    from: new Date().toDateString(),
    to: new Date().toDateString(),
  });
  const generateDateRange = (range: DateRangeOption) => {
    const { value, from, to } = range;

    if (value === 0) setIsOpen(true);
    else {
      if (isOpen) setIsOpen(false);
      const date = new Date();
      date.setDate(date.getDate() - value);

      const fromTimeStamp = from ? getTimeStampString(new Date(from)) : getTimeStampString(date);
      const toTimeSTamp = to ? getTimeStampString(new Date(to)) : getTimeStampString(new Date());

      props.dateValue(
        {
          from: fromTimeStamp,
          to: toTimeSTamp,
        },
        false // value to check if custom filter does not reverse the values for due date range
      );
    }
    // Note: Do not update when custom range is selected as it is not yet applied
    value !== 0 && props.onSelected && props.onSelected(value.toString());
  };
  const customDateFilter = (formDate: any) => {
    const from = formDate['from-datetime-picker'];
    const to = formDate['to-datetime-picker'];

    const fromTimeStamp = from ? (new Date(from).getTime() * 1000).toString() : '';
    const toTimeSTamp = to ? (new Date(to).getTime() * 1000).toString() : ''; // this ternary condtion is added in to avoid setting `toTimeStamp` as `NaN` in case `to` is not selected on date picker

    const newDateRange = {
      from: fromTimeStamp,
      to: toTimeSTamp,
    };

    props.dateValue(newDateRange, true);
    // Set the onSelected value for custom date range
    props.onSelected && props.onSelected('0');
    setCustomDateRange(newDateRange);
  };

  const isBadgeVisible = props?.selected !== '';
  const triggerTestId = props?.placeholder
    ? (props?.placeholder || '').split(' ').join('-').toLowerCase() + '-filter-trigger'
    : 'filter-trigger';

  const dropdownMenuTrigger = (
    <Button
      iconAfter={<ChevronDownIcon label="Down icon" size="medium" />}
      className="trigger-button"
      testId={triggerTestId}
    >
      {props?.placeholder || 'Time period'}
      {isBadgeVisible && (
        <span className="pl-4" data-testid="date-filter-badge">
          <Badge appearance="primary">1</Badge>
        </span>
      )}
    </Button>
  );

  return (
    <div className="date-filter">
      <DropdownMenu trigger={dropdownMenuTrigger} position="bottom left" testId="dateFilter">
        <DropdownItemGroupRadio id="actions" data-testid="date-groupdropdown">
          {dateRangeOptions.map((range, index) => {
            return (
              <DropdownItemRadio
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                key={index} // @ts-ignore
                id={index}
                onClick={() => generateDateRange(range)}
                data-testid={range.text}
                isSelected={props.selected === range.value?.toString()}
              >
                {range.text}
              </DropdownItemRadio>
            );
          })}
        </DropdownItemGroupRadio>
        {isOpen && (
          <Form onSubmit={(formState: unknown) => customDateFilter(formState)}>
            {({ formProps }) => (
              <form {...formProps}>
                <div>
                  <div className="from-datetime-picker">
                    <Field
                      name="from-datetime-picker"
                      data-testId="from-datetime-picker"
                      isRequired={false}
                      defaultValue={customDateRange.from}
                    >
                      {({ fieldProps }) => <DatePicker {...fieldProps} dateFormat="MMM DD, YYYY" />}
                    </Field>
                  </div>
                  <div className="to-datetime-picker">
                    <Field
                      name="to-datetime-picker"
                      data-testId="to-datetime-picker"
                      isRequired={false}
                      defaultValue={customDateRange.to}
                    >
                      {({ fieldProps }) => <DatePicker {...fieldProps} dateFormat="MMM DD, YYYY" />}
                    </Field>
                  </div>
                </div>
                <div className="apply-datetime">
                  <Button type="submit" appearance="primary" testId="apply-filter">
                    Apply
                  </Button>
                </div>
              </form>
            )}
          </Form>
        )}
      </DropdownMenu>
    </div>
  );
};

export default DateFilterWithBadge;
